/**
 * Original InfoPopper, which actually uses Popover to render the info box.
 * This should most likely be used instead of the Popper version, except in special cases
 * because of how Popover will disable scrolling when the infobox is open leading to better experience.
 */

import { IconButton, Popover } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/system';

const StyledText = styled('div')(({ maxWidth }: { maxWidth?: number }) => ({
  fontSize: '1.6rem',
  padding: '1rem',
  maxWidth: `${maxWidth ? maxWidth : '40'}rem`,
  whiteSpace: 'pre-line',
}));

const Button = styled(IconButton)`
  padding: 0 !important;
  margin: 0 !important;
`;

const InfoPopover = ({ text, values, maxWidth, color, size = 'normal' }: IOwnProps): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setAnchorEl(!anchorEl ? event.currentTarget : null);
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement | Element>): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'infopopover' : undefined;
  return (
    <React.Fragment>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <StyledText maxWidth={maxWidth} onClick={handleClose}>
          {typeof text === 'string' ? (
            values ? (
              <FormattedMessage id={text} values={{ value: values }} />
            ) : (
              <FormattedMessage id={text} />
            )
          ) : (
            text
          )}
        </StyledText>
      </Popover>
      <Button onClick={handleClick}>
        {!color ? (
          <div style={{ color: 'white' }}>
            <HelpIcon
              style={{
                padding: 0,
                margin: 0,
                fontSize: size === 'large' ? '5rem' : size === 'small' ? '2rem' : size === 'normal' ? '3rem' : size,
              }}
            />
          </div>
        ) : (
          <HelpIcon
            color={color}
            style={{
              padding: 0,
              margin: 0,
              fontSize: size === 'large' ? '5rem' : size === 'small' ? '2rem' : size === 'normal' ? '3rem' : size,
            }}
          />
        )}
      </Button>
    </React.Fragment>
  );
};

interface IOwnProps {
  text: string | React.JSX.Element;
  values?: string | number; // Mainly for rulm test score popover, can be modified of course for other uses
  maxWidth?: number;
  color?: 'primary' | 'secondary';
  size?: 'small' | 'normal' | 'large' | string;
}

export default InfoPopover;
